import { baseUrl, getTokenFromLocalStorage } from "./Configuration";

export interface BankDetailVerificationResponse {
  receiverName: string;
  receiverEmail: string;
  apartmentTitle: string;
  isValid: boolean;
}

export interface BankDetailSubmission {
  listingId: string;
  receiverId: string;
  bankName: string;
  accountNumber: string;
  ifscOrRoutingCode: string;
  accountHolderName: string;
}

export interface BankDetailResponse {
  Id: string;
  ListingId: string;
  ReceiverId: string;
  ReceiverName: string;
  ReceiverEmail: string;
  BankName: string;
  AccountNumber: string;
  IFSCorRoutingCode: string;
  AccountHolderName: string;
  SubmittedAt: string;
}

export async function verifyBankDetailRequest(
  listingId: string,
  receiverId: string,
): Promise<BankDetailVerificationResponse> {
  const url = `${baseUrl}/api/BankDetail/verify/${listingId}/${receiverId}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.status === 401) {
      localStorage.clear();
      window.location.href = "/login";
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: `HTTP ${response.status}: ${response.statusText}`,
      }));
      throw new Error(
        errorData.message || "Failed to verify bank detail request",
      );
    }

    return await response.json();
  } catch (error: any) {
    console.error("Error verifying bank detail request:", error);
    if (error.message === "Failed to fetch") {
      throw new Error(
        "Unable to connect to server. Please check your connection and try again.",
      );
    }
    throw error;
  }
}

export async function submitBankDetails(
  bankDetail: BankDetailSubmission,
): Promise<{ message: string; bankDetailId: string }> {
  const url = `${baseUrl}/api/BankDetail/submit`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(bankDetail),
    });

    if (response.status === 401) {
      localStorage.clear();
      window.location.href = "/login";
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: `HTTP ${response.status}: ${response.statusText}`,
      }));
      throw new Error(errorData.message || "Failed to submit bank details");
    }

    return await response.json();
  } catch (error: any) {
    console.error("Error submitting bank details:", error);
    if (error.message === "Failed to fetch") {
      throw new Error(
        "Unable to connect to server. Please check your connection and try again.",
      );
    }
    throw error;
  }
}

export async function getAllBankDetails(): Promise<BankDetailResponse[]> {
  const url = `${baseUrl}/api/BankDetail`;

  try {
    const token = await getTokenFromLocalStorage();
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 401) {
      localStorage.clear();
      window.location.href = "/login";
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: `HTTP ${response.status}: ${response.statusText}`,
      }));
      throw new Error(errorData.message || "Failed to fetch all bank details");
    }

    const data = await response.json();
    return data;
  } catch (error: any) {
    if (error.message === "Failed to fetch") {
      throw new Error(
        "Unable to connect to server. Please check your connection and try again.",
      );
    }
    throw error;
  }
}

export async function getBankDetailsByListing(
  listingId: string,
): Promise<BankDetailResponse[]> {
  const url = `${baseUrl}/api/BankDetail/${listingId}`;

  try {
    const token = await getTokenFromLocalStorage();
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 401) {
      localStorage.clear();
      window.location.href = "/login";
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: `HTTP ${response.status}: ${response.statusText}`,
      }));
      throw new Error(
        errorData.message || "Failed to fetch bank details for listing",
      );
    }

    const data = await response.json();
    return data;
  } catch (error: any) {
    if (error.message === "Failed to fetch") {
      throw new Error(
        "Unable to connect to server. Please check your connection and try again.",
      );
    }
    throw error;
  }
}
